import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, FileText, Folder, BookMarked, PenLine, Brush, CloudUpload, Trash2,
  Eraser, Save, Cloud,
} from "lucide-react";
import { idbPutPage, idbGetPage } from "@/lib/idb";
import { AgentDock } from "./AgentDock";

function SketchCanvas({ value, onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [color, setColor] = useState("#111111");
  const [size, setSize] = useState(3);
  const [erase, setErase] = useState(false);

  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = value;
    }
  }, [value]);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (ref.current.width / r.width),
             y: (t.clientY - r.top) * (ref.current.height / r.height) };
  };
  const start = (e) => { drawing.current = true; draw(e); };
  const end = () => { if (!drawing.current) return; drawing.current = false;
    ref.current.getContext("2d").beginPath(); onChange(ref.current.toDataURL("image/png")); };
  const draw = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineWidth = erase ? size * 6 : size;
    ctx.lineCap = "round";
    ctx.strokeStyle = erase ? "#ffffff" : color;
    ctx.globalCompositeOperation = erase ? "destination-out" : "source-over";
    ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
  };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(""); };

  return (
    <div data-testid="notebook-sketch">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input type="color" value={color} onChange={e => { setColor(e.target.value); setErase(false); }} className="w-8 h-8 border-2 border-black rounded" data-testid="sketch-color" />
        <input type="range" min="1" max="12" value={size} onChange={e => setSize(+e.target.value)} data-testid="sketch-size" />
        <button className={`nb-btn text-xs ${erase ? "" : "nb-btn-ghost"}`} onClick={() => setErase(v => !v)} data-testid="sketch-erase"><Eraser size={13} /> Erase</button>
        <button className="nb-btn nb-btn-ghost text-xs" onClick={clear} data-testid="sketch-clear"><Trash2 size={13} /> Clear</button>
      </div>
      <canvas ref={ref} width={520} height={360}
        className="w-full border-2 border-black rounded-lg bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end} />
    </div>
  );
}

export default function PonderrNotebook() {
  const [tree, setTree] = useState([]);
  const [activePage, setActivePage] = useState(null); // {page_id, title, content, sketch, updated_at}
  const [prevSynced, setPrevSynced] = useState(null);
  const [mode, setMode] = useState("write");
  const [showAgent, setShowAgent] = useState(true);
  const editorRef = useRef(null);

  const loadTree = useCallback(async () => {
    try { const r = await api.get("/notebook"); setTree(r.data.notebooks || []); return r.data.notebooks; }
    catch { return []; }
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  const ensureStarter = async () => {
    const nb = (await api.post("/notebook/notebook", { title: "My Notebook" })).data;
    const sec = (await api.post("/notebook/section", { notebook_id: nb.notebook_id, title: "Section 1" })).data;
    await api.post("/notebook/page", { section_id: sec.section_id, title: "New page" });
    await loadTree();
  };

  const openPage = async (pageId) => {
    const cloud = (await api.get(`/notebook/page/${pageId}`)).data;
    const local = await idbGetPage(pageId);
    // Local-first: if IndexedDB copy is newer, show it.
    let page = cloud;
    if (local && local.updated_at && (!cloud.updated_at || local.updated_at > cloud.updated_at)) {
      page = { ...cloud, content: local.content, sketch: local.sketch, updated_at: local.updated_at };
    }
    setActivePage(page);
    setPrevSynced(cloud.updated_at || null);
  };

  const localSave = async (patch) => {
    const next = { ...activePage, ...patch, updated_at: new Date().toISOString() };
    setActivePage(next);
    await idbPutPage({ page_id: next.page_id, title: next.title, content: next.content || "",
                       sketch: next.sketch || "", updated_at: next.updated_at });
  };

  const syncCloud = async () => {
    if (!activePage) return;
    try {
      const r = await api.put(`/notebook/page/${activePage.page_id}`, {
        content: activePage.content || "", sketch: activePage.sketch || "",
        title: activePage.title, updated_at: activePage.updated_at,
        prev_synced_at: prevSynced,
      });
      if (r.data.collision && r.data.resolution === "kept_both") {
        toast.warning("Edit collision — both versions kept as snapshots (Local & Cloud)");
        await loadTree();
      } else if (r.data.resolution === "kept_cloud") {
        toast.info("A newer cloud version was kept");
        setActivePage(p => ({ ...p, content: r.data.content, sketch: r.data.sketch }));
        setPrevSynced(r.data.updated_at);
      } else {
        toast.success("Synced to cloud");
        setPrevSynced(r.data.updated_at);
      }
    } catch { toast.error("Cloud sync failed — your work is safe locally"); }
  };

  const getSelection = () => {
    if (mode === "write" && editorRef.current) {
      const el = editorRef.current;
      const sel = el.value.substring(el.selectionStart, el.selectionEnd);
      if (sel) return sel;
    }
    return (window.getSelection && window.getSelection().toString()) || "";
  };

  const addChild = async (kind, parentId) => {
    if (kind === "notebook") await api.post("/notebook/notebook", { title: "Notebook" });
    if (kind === "section") await api.post("/notebook/section", { notebook_id: parentId, title: "Section" });
    if (kind === "page") { const p = (await api.post("/notebook/page", { section_id: parentId, title: "New page" })).data;
      await loadTree(); openPage(p.page_id); return; }
    await loadTree();
  };

  return (
    <div className="flex h-full" data-testid="ponderr-notebook">
      {/* Tree */}
      <div className="w-52 shrink-0 border-r-2 border-black bg-[#FAF7F0] overflow-y-auto p-2">
        <button className="nb-btn w-full justify-center text-xs mb-2" onClick={() => addChild("notebook")} data-testid="nb-add-notebook"><Plus size={13} /> Notebook</button>
        {!tree.length && <button className="nb-btn nb-btn-secondary w-full text-xs" onClick={ensureStarter} data-testid="nb-starter">Create starter notebook</button>}
        {tree.map(nb => (
          <div key={nb.notebook_id} className="mb-2">
            <div className="flex items-center gap-1 font-black text-xs"><BookMarked size={13} /> {nb.title}
              <button className="ml-auto" onClick={() => addChild("section", nb.notebook_id)} data-testid={`nb-add-section-${nb.notebook_id}`}><Plus size={12} /></button>
            </div>
            {nb.sections.map(s => (
              <div key={s.section_id} className="ml-2 mt-1">
                <div className="flex items-center gap-1 text-[11px] font-bold text-slate-600"><Folder size={11} /> {s.title}
                  <button className="ml-auto" onClick={() => addChild("page", s.section_id)} data-testid={`nb-add-page-${s.section_id}`}><Plus size={11} /></button>
                </div>
                {s.pages.map(p => (
                  <button key={p.page_id} onClick={() => openPage(p.page_id)}
                    className={`ml-3 mt-1 flex items-center gap-1 text-[11px] hover:underline text-left w-full ${activePage?.page_id === p.page_id ? "font-bold" : ""} ${p.is_snapshot ? "italic text-amber-700" : ""}`}
                    data-testid={`nb-page-${p.page_id}`}>
                    <FileText size={10} /> {p.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Editor */}
      <div className="flex-1 flex flex-col min-w-0">
        {!activePage ? (
          <div className="flex-1 grid place-items-center text-sm opacity-60">Select or create a page to start writing.</div>
        ) : (
          <>
            <div className="p-2 border-b-2 border-black flex items-center gap-2 flex-wrap bg-white">
              <input className="nb-input flex-1 min-w-[120px] font-bold text-sm" value={activePage.title || ""}
                onChange={e => localSave({ title: e.target.value })} data-testid="nb-page-title" />
              <button className={`nb-btn text-xs ${mode === "write" ? "" : "nb-btn-ghost"}`} onClick={() => setMode("write")} data-testid="nb-mode-write"><PenLine size={13} /> Write</button>
              <button className={`nb-btn text-xs ${mode === "sketch" ? "" : "nb-btn-ghost"}`} onClick={() => setMode("sketch")} data-testid="nb-mode-sketch"><Brush size={13} /> Sketch</button>
              <button className="nb-btn nb-btn-secondary text-xs" onClick={syncCloud} data-testid="nb-sync"><CloudUpload size={13} /> Sync</button>
              <button className="nb-btn nb-btn-ghost text-xs opacity-50 cursor-not-allowed" disabled title="Connect Microsoft Account to enable OneNote export" data-testid="nb-onenote"><Cloud size={13} /> OneNote</button>
              <button className={`nb-btn text-xs ${showAgent ? "" : "nb-btn-ghost"}`} onClick={() => setShowAgent(v => !v)} data-testid="nb-toggle-agent">🤖 Tutor</button>
            </div>
            <div className="flex-1 flex min-h-0">
              <div className="flex-1 p-3 overflow-y-auto">
                {mode === "write" ? (
                  <textarea ref={editorRef} className="w-full h-full min-h-[360px] nb-input text-sm resize-none font-mono"
                    placeholder="# Write your notes in Markdown…" value={activePage.content || ""}
                    onChange={e => localSave({ content: e.target.value })} data-testid="nb-editor" />
                ) : (
                  <SketchCanvas value={activePage.sketch || ""} onChange={v => localSave({ sketch: v })} />
                )}
                <div className="text-[10px] opacity-50 mt-1 flex items-center gap-1"><Save size={10} /> Saved locally (IndexedDB) · {activePage.updated_at?.slice(11, 19)}</div>
              </div>
              {showAgent && <div className="w-80 shrink-0 border-l-2 border-black"><AgentDock getSelection={getSelection} /></div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
